import "./App.css";
import NotFound from "@/pages/not-found/NotFound";
import { legacyMcpRedirects, routesWithSubroutes } from "./route-elements";

import { NuqsAdapter } from "nuqs/adapters/react-router/v8";
import { Toaster } from "@/components/ui/Sonner";
import { ConfigProvider } from "@/components/ui/context/ConfigContext";
import { TooltipProvider } from "@/components/ui/Tooltip";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { AppLayout, LoginCheck, OrgLayout } from "./components/app-layout.tsx";
import { ModeSwitchProvider } from "./components/mode-switch-stage.tsx";
import { CommandPalette } from "./components/command-palette";
import {
  orgRoutesToNavActions,
  routeToNavAction,
} from "./components/command-palette/navigation-actions";
import { GuideEntryRedirect } from "./components/project-guide/GuideEntryRedirect";
import {
  getRecentLabelOverride,
  pageLabel,
  recordVisit,
  RECENTS_LABEL_OVERRIDE_EVENT,
  removeVisitsMatching,
  shouldRemoveRestrictedRecents,
} from "./components/command-palette/recentlyVisited";
import { useIsPlatformAdmin, useUser } from "./contexts/Auth";
import { useProjectNavRoutes } from "./hooks/useProjectNavRoutes";
import { useRBAC } from "./hooks/useRBAC";
import { useKillswitchAccess } from "./hooks/useKillswitchAccess";
import { AuthProvider, ProjectProvider } from "./contexts/AuthProvider.tsx";
import { useCommandPalette } from "./contexts/CommandPalette";
import { CommandPaletteProvider } from "./contexts/CommandPaletteProvider";
import { useSlugs } from "./contexts/Sdk.tsx";
import { SdkProvider } from "./contexts/SdkProvider.tsx";
import { TelemetryProvider } from "./contexts/TelemetryProvider.tsx";
import { usePageTitle } from "./hooks/use-page-title";
import { PREFERRED_THEME_STORAGE_KEY } from "./lib/local-storage-keys";
import {
  rememberPreservedStorageKey,
  restorePreservedStorageBackup,
} from "./lib/logout-storage";
import CliCallback from "./pages/cli/CliCallback";
import ShadowMCPRequestAccess from "./pages/shadow-mcp/RequestAccess";
import RiskPolicyChallengeAcknowledge from "./pages/risk-policy-challenge/Acknowledge";
import { BlockPage } from "./pages/blocks/BlockDetail";
import { SHARED_SKILL_BASE_PATH } from "./pages/skills/share-link";
import { SharedSkillPage } from "./pages/skills/SharedSkillPage";
import SwitchOrg from "./pages/demo/SwitchOrg";
import TrialEnded from "./pages/demo/TrialEnded";
import { pageHrefs, withOrgPages, withProjectPages } from "./route-pages";
import { useRoutes, useOrgRoutes } from "./routes";
import { isCliAuthFlowLocation } from "@/lib/cli-auth-flow";

// Logout may have navigated here after Clear-Site-Data emptied localStorage.
// theme-init.ts already restores when it can; this covers the module path
// (tests, and any load that skipped the classic script).
restorePreservedStorageBackup();

export default function App(): JSX.Element {
  // Initialize from storage so React/Moonshine match the theme the pre-paint
  // script (loaded from index.html) already applied to <html> — avoids a flash.
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    try {
      return localStorage.getItem(PREFERRED_THEME_STORAGE_KEY) === "dark"
        ? "dark"
        : "light";
    } catch {
      return "light";
    }
  });

  const applyTheme = (theme: "light" | "dark") => {
    const root = document.documentElement;
    if (root.classList.contains(theme)) return;
    root.classList.add(theme);
    root.classList.remove(theme === "dark" ? "light" : "dark");

    localStorage.setItem(PREFERRED_THEME_STORAGE_KEY, theme);
    rememberPreservedStorageKey(PREFERRED_THEME_STORAGE_KEY, theme);

    setTheme(theme);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem(PREFERRED_THEME_STORAGE_KEY) as
      | "light"
      | "dark"
      | null;

    // Light mode is the default; only honor an explicit prior choice. The
    // system color-scheme preference no longer forces dark on first load.
    const initialTheme = savedTheme || "light";
    applyTheme(initialTheme);
  }, []);

  return (
    <ConfigProvider theme={theme} setTheme={applyTheme}>
      <TooltipProvider>
        <TelemetryProvider>
          <CommandPaletteProvider>
            <BrowserRouter>
              <NuqsAdapter>
                <SdkProvider>
                  <AppContent />
                  <Toaster />
                  <CommandPalette />
                </SdkProvider>
              </NuqsAdapter>
            </BrowserRouter>
          </CommandPaletteProvider>
        </TelemetryProvider>
      </TooltipProvider>
    </ConfigProvider>
  );
}

function AppContent() {
  /**
   * NOTE(cjea): Do not wrap CliCallback in an AuthProvider.
   *
   * CLI requests don't include a redirect URL, so AuthProvider wouldn't know
   * where to send authenticated users. Instead, these components handle the flow:
   *
   * 1. Component receives an unauthenticated request.
   * 2. Sets the redirect query param to its current URL.
   * 3. Sends the user through the standard login flow via AuthHandler.
   * 4. Authenticated user is redirected back to the component.
   */
  const cliFlow = useCliAuthFlow();

  if (cliFlow) {
    return (
      <CliCallback
        keyScope={cliFlow.keyScope}
        localCallbackUrl={cliFlow.cliCallbackUrl}
        projectSlug={cliFlow.projectSlug}
        organizationId={cliFlow.organizationId}
        codeChallenge={cliFlow.codeChallenge}
        codeChallengeMethod={cliFlow.codeChallengeMethod}
        callbackMethod={cliFlow.callbackMethod}
      />
    );
  }

  return (
    <AuthProvider>
      <ProjectProvider>
        <RouteProvider />
      </ProjectProvider>
    </AuthProvider>
  );
}

const RouteProvider = () => {
  const routes = useRoutes();
  const orgRoutes = useOrgRoutes();
  const pagedRoutes = useMemo(() => withProjectPages(routes), [routes]);
  const pagedOrgRoutes = useMemo(() => withOrgPages(orgRoutes), [orgRoutes]);
  const { addActions, removeActions } = useCommandPalette();
  const { orgSlug, projectSlug } = useSlugs();
  const location = useLocation();
  const projectNavRoutes = useProjectNavRoutes();
  const { hasAnyScope } = useRBAC();
  const killswitchAccess = useKillswitchAccess();
  // RouteProvider is inside AuthProvider, so reuse the already-fetched session
  // instead of issuing another auth.info request.
  const recentsUserId = useUser().id || undefined;

  // Update document title based on active route
  usePageTitle(routes, orgRoutes);

  // Ctrl+Shift+D toggles between the Platform Admin pages and wherever the
  // user was working — carried over from the retired floating Developer
  // Toolkit, which used the same chord to unhide itself. First press remembers
  // the current page and jumps to the Platform Admin overview; pressing it
  // again from any Platform Admin page returns to the remembered page. Same
  // audience rule as the pages: platform admins, or anyone in dev.
  const isPlatformAdmin = useIsPlatformAdmin();
  const goToPlatformAdmin = orgRoutes.platformAdminOverview.goTo;
  const navigate = useNavigate();
  const platformAdminReturnPath = useRef<string | null>(null);
  useEffect(() => {
    if (!(import.meta.env.DEV || isPlatformAdmin)) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey && e.shiftKey && e.key === "D")) return;
      e.preventDefault();
      // Test the route segment below :orgSlug specifically — an org whose
      // slug is literally "platform-admin" must not be mistaken for the
      // admin pages (which would turn the first press into a no-op).
      const onPlatformAdminPage =
        location.pathname.split("/").filter(Boolean)[1] === "platform-admin";
      if (onPlatformAdminPage) {
        const returnPath = platformAdminReturnPath.current;
        platformAdminReturnPath.current = null;
        if (returnPath) void navigate(returnPath);
      } else {
        platformAdminReturnPath.current = location.pathname + location.search;
        goToPlatformAdmin();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isPlatformAdmin, goToPlatformAdmin, location, navigate]);

  useEffect(() => {
    if (
      !shouldRemoveRestrictedRecents({
        canAccess: killswitchAccess.canAccess,
        isLoading: killswitchAccess.isLoading,
      }) ||
      !recentsUserId
    )
      return;
    const killswitchHref = orgRoutes.killswitch.href();
    removeVisitsMatching(
      recentsUserId,
      orgSlug,
      projectSlug,
      (entry) =>
        entry.href === killswitchHref ||
        entry.href.startsWith(`${killswitchHref}/`),
    );
  }, [
    killswitchAccess.canAccess,
    killswitchAccess.isLoading,
    recentsUserId,
    orgRoutes.killswitch,
    orgSlug,
    projectSlug,
  ]);

  // Record the visited page for the command palette's "Recently Visited"
  // section. Stored client-side (localStorage), scoped per workspace.
  // matchesCurrent uses exact segment counts, so the active top-level route is
  // the real current page. We record the EXACT path (not the section base) so a
  // detail page like /sources/externalmcp/notion is its own entry — labelled by
  // the item slug ("notion") rather than the section ("Sources").
  useEffect(() => {
    // Wait until the user id resolves before recording — otherwise the early
    // write lands on the shared anonymous key instead of the per-user one.
    // recentsUserId is a dependency, so the effect re-runs (and records the
    // current page) as soon as the session loads.
    if (!recentsUserId) return;
    // The killswitch addresses only forward to the identity they belong to,
    // so recording one would file a redirect under the reader's recent pages
    // instead of the page they actually ended up on.
    if (orgRoutes.killswitch.active) return;
    const active =
      Object.values(routes).find((r) => r.active && !r.external) ??
      Object.values(orgRoutes).find((r) => r.active && !r.external);
    if (!active) return;
    const iconName = (active as unknown as { icon?: string }).icon;
    // Recents are page-level: record the pathname without query params so a
    // page and its deep-linked item state (e.g. ?policy=<id>) collapse into a
    // single entry instead of appearing as duplicates with the same label.
    const href = location.pathname;
    const record = (label: string) =>
      recordVisit(recentsUserId, orgSlug, projectSlug, {
        label,
        href,
        icon: typeof iconName === "string" ? iconName : undefined,
      });

    // Prefer a label a detail page registered for this href (e.g. the assistant
    // or conversation name) over the URL-derived fallback, which for id-keyed
    // pages is just the section title until the override arrives.
    record(
      getRecentLabelOverride(href) ??
        pageLabel(active.title, active.href(), location.pathname),
    );

    // A detail page may register its label *after* this first record (its data
    // loads async). Re-record when that happens so the entry upgrades from the
    // section title to the resource name.
    const onOverride = (event: Event) => {
      const detail = (event as CustomEvent<{ href: string }>).detail;
      if (detail?.href !== href) return;
      const label = getRecentLabelOverride(href);
      if (label) record(label);
    };
    window.addEventListener(RECENTS_LABEL_OVERRIDE_EVENT, onOverride);
    return () =>
      window.removeEventListener(RECENTS_LABEL_OVERRIDE_EVENT, onOverride);
  }, [
    location.pathname,
    routes,
    orgRoutes,
    recentsUserId,
    orgSlug,
    projectSlug,
    killswitchAccess.canAccess,
  ]);

  // Register command palette navigation actions. Project "Pages" mirror the
  // left sidebar exactly (same source, same order) so the palette only offers
  // pages a user can actually reach from the nav. Project pages register only
  // once a project is selected (their goTo() needs the :projectSlug); org pages
  // are always available.
  useEffect(() => {
    // Nav routes come from their own useRoutes call, so they are matched to
    // the page map by href. Nested nav routes (People, Agents) count too.
    const projectPageHrefs = pageHrefs(Object.values(pagedRoutes));
    const projectActions = projectSlug
      ? projectNavRoutes
          .filter(
            ({ route, scope, resourceId, projectId }) =>
              !route.external &&
              projectPageHrefs.has(route.href()) &&
              route.title &&
              // Mirror the sidebar's per-page scope gating so the palette never
              // offers (nor navigates to) pages the user can't access.
              (scope.length === 0 || hasAnyScope(scope, resourceId, projectId)),
          )
          .map(({ route }) =>
            routeToNavAction(route, "Pages", `nav-page-${route.url || "home"}`),
          )
      : [];
    // Platform admin surfaces (platform-admin/* and the platform RIP catalog)
    // follow the sidebar's audience rule: platform admins, or anyone in local
    // dev. Filtering here keeps them out of the palette for regular users.
    const showPlatformAdmin = import.meta.env.DEV || isPlatformAdmin;
    const orgActions = orgRoutesToNavActions(
      pagedOrgRoutes,
      showPlatformAdmin,
      hasAnyScope,
    );

    const allActions = [...projectActions, ...orgActions];
    addActions(allActions);

    return () => {
      removeActions(allActions.map((a) => a.id));
    };
  }, [
    projectNavRoutes,
    pagedRoutes,
    pagedOrgRoutes,
    projectSlug,
    hasAnyScope,
    isPlatformAdmin,
    addActions,
    removeActions,
  ]);

  const routeElements = useMemo(() => {
    const allRoutes = Object.values(pagedRoutes);
    const unauthenticatedRoutes = allRoutes.filter(
      (route) => route.unauthenticated,
    );
    const outsideStructureRoutes = allRoutes.filter(
      (route) => route.outsideMainLayout,
    );
    const authenticatedRoutes = allRoutes.filter(
      (route) =>
        !outsideStructureRoutes.includes(route) && !route.unauthenticated,
    );

    const orgRouteValues = Object.values(pagedOrgRoutes);
    const orgHomeRoute = orgRouteValues.find((r) => r.url === "");
    const outsideOrgLayoutRoutes = orgRouteValues.filter(
      (route) => route.outsideMainLayout,
    );
    const otherOrgRoutes = orgRouteValues.filter(
      (r) => r.url !== "" && !r.outsideMainLayout,
    );

    return (
      <Routes>
        {/* Register these unauthenticated paths outside of root layout */}
        {routesWithSubroutes(unauthenticatedRoutes)}
        <Route path="/switch-org" element={<LoginCheck />}>
          <Route index element={<SwitchOrg />} />
        </Route>
        {/* Outside the app layout because it is a full-page gate, but behind
            LoginCheck: an expired trial still has a session, and a logged-out
            visitor has no trial to talk about. */}
        <Route path="/trial-ended" element={<LoginCheck />}>
          <Route index element={<TrialEnded />} />
        </Route>
        <Route
          path="/shadow-mcp/request"
          element={<ShadowMCPRequestAccess />}
        />
        <Route
          path="/risk-policy-bypass/request"
          element={<ShadowMCPRequestAccess />}
        />
        <Route
          path="/risk-policy-challenge/acknowledge"
          element={<RiskPolicyChallengeAcknowledge />}
        />
        <Route path="/blocks/:id" element={<BlockPage />} />
        <Route
          path={`${SHARED_SKILL_BASE_PATH}/:token`}
          element={<SharedSkillPage />}
        />
        <Route path="/" element={<LoginCheck />}>
          <Route path="guide" element={<GuideEntryRedirect />} />
          <Route path=":orgSlug/projects/:projectSlug">
            {routesWithSubroutes(outsideStructureRoutes)}
          </Route>
          <Route path=":orgSlug/projects/:projectSlug" element={<AppLayout />}>
            {routesWithSubroutes(authenticatedRoutes)}
            {legacyMcpRedirects}
            <Route path="*" element={<NotFound />} />
          </Route>
          {/* Org routes that render without OrgLayout (full-screen standalone pages) */}
          <Route path=":orgSlug">
            {routesWithSubroutes(outsideOrgLayoutRoutes)}
          </Route>
          <Route path=":orgSlug" element={<OrgLayout />}>
            {orgHomeRoute?.component && (
              <Route index element={<orgHomeRoute.component />} />
            )}
            {routesWithSubroutes(otherOrgRoutes)}
          </Route>
        </Route>
      </Routes>
    );
  }, [pagedRoutes, pagedOrgRoutes]);

  // Above the router: the mode-switch animation spans the route swap between
  // its shrink and zoom beats, so its state has to outlive that navigation.
  return <ModeSwitchProvider>{routeElements}</ModeSwitchProvider>;
};

type LocalAuthFlow = {
  cliCallbackUrl: string;
  keyScope: "producer" | "hooks";
  projectSlug: string | null;
  organizationId: string | null;
  codeChallenge: string | null;
  codeChallengeMethod: string | null;
  callbackMethod: "get" | "post";
};

function useCliAuthFlow(): LocalAuthFlow | null {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const cliCallbackUrl = searchParams.get("cli_callback_url");
  const keyScope = searchParams.get("key_scope");
  const projectSlug = searchParams.get("project");
  const organizationId = searchParams.get("organization_id");
  const codeChallenge = searchParams.get("code_challenge");
  const codeChallengeMethod = searchParams.get("code_challenge_method");
  const callbackMethod = searchParams.get("callback_method");

  if (
    cliCallbackUrl &&
    isCliAuthFlowLocation(location.pathname, location.search)
  ) {
    return {
      cliCallbackUrl,
      keyScope: keyScope === "hooks" ? "hooks" : "producer",
      projectSlug,
      organizationId,
      codeChallenge,
      codeChallengeMethod,
      callbackMethod: callbackMethod === "post" ? "post" : "get",
    };
  }

  return null;
}
